import { useState, useRef, useEffect, useCallback } from "react";
import { 
  Image, 
  Video, 
  Send, 
  Calendar,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  Hash,
  Sparkles,
  Copy,
  Plus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";
import FacebookPreview from "@/components/previews/FacebookPreview";
import InstagramPreview from "@/components/previews/InstagramPreview";
import TikTokPreview from "@/components/previews/TikTokPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform definitions
const PLATFORMS = [
  { 
    id: "facebook", 
    name: "Facebook", 
    color: "#1877F2",
    icon: () => (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    )
  },
  { 
    id: "instagram", 
    name: "Instagram", 
    color: "#E1306C",
    gradient: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
    icon: () => (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    )
  },
  { 
    id: "tiktok", 
    name: "TikTok", 
    color: "#000000",
    icon: () => (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
      </svg>
    )
  }
];

export default function CreatePost() {
  const [content, setContent] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState(["facebook"]);
  const [media, setMedia] = useState([]);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [activePreview, setActivePreview] = useState("facebook");
  const fileInputRef = useRef(null);
  
  // Hashtag suggestions state
  const [hashtagSuggestions, setHashtagSuggestions] = useState({});
  const [detectedCategories, setDetectedCategories] = useState([]);
  const [loadingHashtags, setLoadingHashtags] = useState(false);
  const [showHashtagPanel, setShowHashtagPanel] = useState(false);

  // Debounced hashtag suggestion fetching
  const fetchHashtagSuggestions = useCallback(async (text, platforms) => {
    if (!text || text.length < 10 || platforms.length === 0) {
      setHashtagSuggestions({});
      setDetectedCategories([]);
      return;
    }

    setLoadingHashtags(true);
    try {
      const response = await axios.post(`${API}/hashtags/suggest`, {
        content: text,
        platforms: platforms
      });
      setHashtagSuggestions(response.data.suggestions);
      setDetectedCategories(response.data.detected_categories);
    } catch (error) {
      console.error("Error fetching hashtag suggestions:", error);
    } finally {
      setLoadingHashtags(false);
    }
  }, []);

  // Debounce effect for hashtag suggestions
  useEffect(() => {
    const timer = setTimeout(() => {
      if (content.length >= 10) {
        fetchHashtagSuggestions(content, selectedPlatforms);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [content, selectedPlatforms, fetchHashtagSuggestions]);

  const handlePlatformToggle = (platformId) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platformId)) {
        return prev.filter(p => p !== platformId);
      }
      return [...prev, platformId];
    });
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    for (const file of files) {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");

      if (!isImage && !isVideo) {
        toast.error("Seuls les images et vidéos sont acceptés");
        continue;
      }

      const url = URL.createObjectURL(file);
      setMedia(prev => [...prev, {
        id: Date.now().toString(),
        type: isImage ? "image" : "video",
        url: url,
        filename: file.name,
        file_size: file.size
      }]);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeMedia = (id) => {
    setMedia(prev => prev.filter(m => m.id !== id));
  };

  const addHashtagToContent = (hashtag) => {
    if (!content.includes(hashtag)) {
      setContent(prev => prev + (prev.endsWith(" ") || prev === "" ? "" : " ") + hashtag);
      toast.success(`${hashtag} ajouté`);
    } else {
      toast.info("Ce hashtag est déjà dans votre post");
    }
  };

  const addAllHashtagsForPlatform = (platform) => {
    const tags = hashtagSuggestions[platform] || [];
    const newTags = tags.filter(tag => !content.includes(tag));
    if (newTags.length > 0) {
      setContent(prev => prev + (prev.endsWith(" ") || prev === "" ? "" : "\n\n") + newTags.join(" "));
      toast.success(`${newTags.length} hashtags ajoutés pour ${platform}`);
    } else {
      toast.info("Tous les hashtags sont déjà dans votre post");
    }
  };

  const copyHashtagsToClipboard = (platform) => {
    const tags = hashtagSuggestions[platform] || [];
    navigator.clipboard.writeText(tags.join(" "));
    toast.success("Hashtags copiés dans le presse-papier");
  };

  const handlePublish = async () => {
    if (!content.trim()) {
      toast.error("Veuillez écrire un contenu pour votre post");
      return;
    }

    if (selectedPlatforms.length === 0) {
      toast.error("Veuillez sélectionner au moins une plateforme");
      return;
    }

    setIsPublishing(true);

    try {
      const postData = {
        content: content.trim(),
        platforms: selectedPlatforms,
        media: media,
        scheduled_at: scheduledDate ? scheduledDate.toISOString() : null
      };

      const createRes = await axios.post(`${API}/posts`, postData);
      const postId = createRes.data.id;

      if (scheduledDate) {
        toast.success("Post planifié avec succès!", {
          description: `Publication prévue le ${format(scheduledDate, "d MMMM yyyy 'à' HH:mm", { locale: fr })}`
        });
      } else {
        const publishRes = await axios.post(`${API}/posts/${postId}/publish`);
        const results = publishRes.data.results;

        let successCount = 0;
        let failCount = 0;

        Object.entries(results).forEach(([platform, result]) => {
          if (result.status === "success") {
            successCount++;
          } else {
            failCount++;
            toast.error(`Échec sur ${platform}`, {
              description: result.error
            });
          }
        });

        if (successCount > 0) {
          toast.success(`Publié sur ${successCount} plateforme(s)!`);
        }
      }

      setContent("");
      setMedia([]);
      setScheduledDate(null);
      setHashtagSuggestions({});
      setDetectedCategories([]);

    } catch (error) {
      console.error("Error publishing:", error);
      toast.error("Erreur lors de la publication", {
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const getPlatformColor = (platform) => {
    const colors = {
      facebook: "#1877F2",
      instagram: "#E1306C",
      tiktok: "#000000"
    };
    return colors[platform] || "#666";
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="create-post-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Créer un post</h1>
        <p className="text-muted-foreground mt-1">Publiez sur plusieurs réseaux en un clic</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Editor Section */}
        <div className="space-y-6">
          {/* Content Input */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Contenu du post</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Qu'avez-vous à partager aujourd'hui ?"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-[150px] resize-none text-base"
                data-testid="post-content-input"
              />
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{content.length} caractères</span>
                <span className={content.length > 2200 ? "text-red-500" : ""}>
                  {content.length > 2200 ? "Limite Instagram dépassée" : ""}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Hashtag Suggestions */}
          <Card className="border-primary/20">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Suggestions de hashtags
                </CardTitle>
                {loadingHashtags && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent>
              {content.length < 10 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Écrivez au moins 10 caractères pour obtenir des suggestions de hashtags
                </p>
              ) : Object.keys(hashtagSuggestions).length === 0 && !loadingHashtags ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Sélectionnez au moins une plateforme pour voir les suggestions
                </p>
              ) : (
                <div className="space-y-4">
                  {/* Detected Categories */}
                  {detectedCategories.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-muted-foreground">Catégories détectées:</span>
                      {detectedCategories.map(cat => (
                        <Badge key={cat} variant="secondary" className="text-xs capitalize">
                          {cat}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Suggestions by Platform */}
                  {selectedPlatforms.map(platform => {
                    const suggestions = hashtagSuggestions[platform] || [];
                    if (suggestions.length === 0) return null;
                    
                    return (
                      <div key={platform} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-6 h-6 rounded flex items-center justify-center text-white text-xs"
                              style={{ background: getPlatformColor(platform) }}
                            >
                              <Hash className="w-3 h-3" />
                            </div>
                            <span className="text-sm font-medium capitalize">{platform}</span>
                            <Badge variant="outline" className="text-xs">
                              {suggestions.length} suggestions
                            </Badge>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => copyHashtagsToClipboard(platform)}
                              className="h-7 px-2"
                              data-testid={`copy-hashtags-${platform}`}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => addAllHashtagsForPlatform(platform)}
                              className="h-7 px-2 gap-1"
                              data-testid={`add-all-hashtags-${platform}`}
                            >
                              <Plus className="w-3 h-3" />
                              Tout ajouter
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {suggestions.map(tag => (
                            <button
                              key={tag}
                              onClick={() => addHashtagToContent(tag)}
                              className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                content.includes(tag) 
                                  ? "bg-primary text-primary-foreground" 
                                  : "bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground"
                              }`}
                              data-testid={`hashtag-${tag.replace('#', '')}`}
                            >
                              {tag}
                              {content.includes(tag) && <CheckCircle className="w-3 h-3 inline ml-1" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Media Upload */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Médias</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="media-upload"
              />
              
              {media.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {media.map((item) => (
                    <div key={item.id} className="relative group rounded-xl overflow-hidden aspect-square bg-muted">
                      {item.type === "image" ? (
                        <img src={item.url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <video src={item.url} className="w-full h-full object-cover" />
                      )}
                      <button
                        onClick={() => removeMedia(item.id)}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-black/60 text-white text-xs">
                        {item.type === "image" ? <Image className="w-3 h-3 inline mr-1" /> : <Video className="w-3 h-3 inline mr-1" />}
                        {item.type}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 gap-2"
                  data-testid="upload-media-btn"
                >
                  <Image className="w-4 h-4" />
                  Ajouter image
                </Button>
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 gap-2"
                  data-testid="upload-video-btn"
                >
                  <Video className="w-4 h-4" />
                  Ajouter vidéo
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Platform Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Plateformes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {PLATFORMS.map((platform) => (
                  <div
                    key={platform.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      selectedPlatforms.includes(platform.id)
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-muted-foreground/30"
                    }`}
                    onClick={() => handlePlatformToggle(platform.id)}
                    data-testid={`platform-${platform.id}`}
                  >
                    <Checkbox
                      checked={selectedPlatforms.includes(platform.id)}
                      onCheckedChange={() => handlePlatformToggle(platform.id)}
                    />
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                      style={{ background: platform.gradient || platform.color }}
                    >
                      <platform.icon />
                    </div>
                    <div className="flex-1">
                      <Label className="font-semibold cursor-pointer">{platform.name}</Label>
                      <p className="text-sm text-muted-foreground">
                        {platform.id === "facebook" && "Page Facebook"}
                        {platform.id === "instagram" && "Feed & Reels"}
                        {platform.id === "tiktok" && "Vidéos TikTok"}
                      </p>
                    </div>
                    {selectedPlatforms.includes(platform.id) && (
                      <CheckCircle className="w-5 h-5 text-primary" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Schedule */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Planification</CardTitle>
            </CardHeader>
            <CardContent>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full justify-start gap-2 ${scheduledDate ? "text-foreground" : "text-muted-foreground"}`}
                    data-testid="schedule-btn"
                  >
                    <Calendar className="w-4 h-4" />
                    {scheduledDate 
                      ? format(scheduledDate, "d MMMM yyyy 'à' HH:mm", { locale: fr })
                      : "Planifier pour plus tard"
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={scheduledDate}
                    onSelect={setScheduledDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                  {scheduledDate && (
                    <div className="p-3 border-t">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setScheduledDate(null)}
                        className="w-full"
                      >
                        Effacer la planification
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </CardContent>
          </Card>

          {/* Publish Button */}
          <Button
            onClick={handlePublish}
            disabled={isPublishing || !content.trim() || selectedPlatforms.length === 0}
            className="w-full h-12 text-base gap-2"
            data-testid="publish-btn"
          >
            {isPublishing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Publication en cours...
              </>
            ) : scheduledDate ? (
              <>
                <Calendar className="w-5 h-5" />
                Planifier la publication
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Publier maintenant
              </>
            )}
          </Button>
        </div>

        {/* Preview Section */}
        <div className="space-y-4">
          <Card className="sticky top-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Prévisualisation</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={activePreview} onValueChange={setActivePreview}>
                <TabsList className="grid grid-cols-3 mb-4">
                  <TabsTrigger value="facebook" className="gap-2" data-testid="preview-facebook-tab">
                    <div className="w-4 h-4 rounded bg-[#1877F2] flex items-center justify-center text-white text-[10px]">f</div>
                    Facebook
                  </TabsTrigger>
                  <TabsTrigger value="instagram" className="gap-2" data-testid="preview-instagram-tab">
                    <div className="w-4 h-4 rounded bg-gradient-to-br from-[#f09433] via-[#dc2743] to-[#bc1888]" />
                    Instagram
                  </TabsTrigger>
                  <TabsTrigger value="tiktok" className="gap-2" data-testid="preview-tiktok-tab">
                    <div className="w-4 h-4 rounded bg-black" />
                    TikTok
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="facebook" className="mt-0">
                  <FacebookPreview content={content} media={media} />
                </TabsContent>

                <TabsContent value="instagram" className="mt-0">
                  <InstagramPreview content={content} media={media} />
                </TabsContent>

                <TabsContent value="tiktok" className="mt-0">
                  <TikTokPreview content={content} media={media} />
                </TabsContent>
              </Tabs>

              {!selectedPlatforms.includes(activePreview) && (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Plateforme non sélectionnée</p>
                    <p className="text-amber-700">Ce post ne sera pas publié sur {activePreview.charAt(0).toUpperCase() + activePreview.slice(1)}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
